import { VocabularyWord, VocabularyFormData, CreateVocabularyData, UpdateVocabularyData, AddTranslationData, UpdateTranslationData, VocabularyTranslation } from '../types/Vocabulary';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

class VocabularyService {
  // Create a new vocabulary word
  async createWord(userId: string, wordData: VocabularyFormData): Promise<{ success: boolean; word?: VocabularyWord; message?: string }> {
    try {
      const data: CreateVocabularyData = {
        user_id: userId,
        word: wordData.word,
        language: wordData.language,
        pronunciation: wordData.pronunciation || undefined,
        example_sentence: wordData.example_sentence || undefined,
        notes: wordData.notes || undefined,
        category: wordData.category || undefined,
        translations: wordData.translations || undefined
      };

      const response = await makeAuthenticatedRequest('/vocabulary', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating vocabulary word:', error);
      throw error;
    }
  }

  // Get vocabulary word by ID
  async getWordById(wordId: string): Promise<{ success: boolean; word?: VocabularyWord }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/${wordId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching vocabulary word:', error);
      throw error;
    }
  }

  // Get all vocabulary words for a user
  async getUserWords(userId: string): Promise<{ success: boolean; words?: VocabularyWord[]; count?: number }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/user/${userId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user vocabulary words:', error);
      throw error;
    }
  }

  // Update a vocabulary word
  async updateWord(updateData: UpdateVocabularyData): Promise<{ success: boolean; message?: string; word?: VocabularyWord }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/vocabulary/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating vocabulary word:', error);
      throw error;
    }
  }

  // Delete a vocabulary word
  async deleteWord(wordId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/${wordId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting vocabulary word:', error);
      throw error;
    }
  }

  // Add a translation to an existing word
  async addTranslation(wordId: string, translationData: AddTranslationData): Promise<{ success: boolean; message?: string; translation?: VocabularyTranslation }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/${wordId}/translations`, {
        method: 'POST',
        body: JSON.stringify(translationData)
      });
      
      return response;
    } catch (error) {
      console.error('Error adding translation:', error);
      throw error;
    }
  }

  // Update a translation
  async updateTranslation(wordId: string, translationId: string, translationData: UpdateTranslationData): Promise<{ success: boolean; message?: string; translation?: VocabularyTranslation }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/${wordId}/translations/${translationId}`, {
        method: 'PUT',
        body: JSON.stringify(translationData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating translation:', error);
      throw error;
    }
  }

  // Delete a translation
  async deleteTranslation(wordId: string, translationId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/vocabulary/${wordId}/translations/${translationId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting translation:', error);
      throw error;
    }
  }
}

export const vocabularyService = new VocabularyService();

